import React, { useState } from "react";
import { toast } from "sonner";
import { Send, Loader2, CheckCircle2 } from "lucide-react";

const inputBase =
  "w-full bg-[#F3F7F0] border border-transparent rounded-2xl px-5 py-4 text-[#0B132B] placeholder:text-[#758A9F] focus:outline-none focus:ring-2 focus:ring-[#FF6B6B] focus:bg-white transition";

export default function EnrollmentForm() {
  const [form, setForm] = useState({
    name: "",
    feedback: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const change = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.feedback) {
      toast.error("Please add your name and feedback.");
      return;
    }
    setLoading(true);
    try {
      const response = await fetch("https://formspree.io/f/xpqndaew", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: form.name,
          feedback: form.feedback,
        }),
      });

      if (!response.ok) {
        throw new Error("Formspree submission failed");
      }
      toast.success("Thanks for the review! It helps us improve.");
      setSuccess(true);
      setForm({ name: "", feedback: "" });
    } catch (err) {
      console.error(err);
      toast.error("Could not submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="enroll"
      data-testid="enrollment-section"
      className="px-4 sm:px-6 md:px-12 lg:px-16 py-20 sm:py-24 md:py-32"
      style={{ background: "var(--bg-secondary)" }}
    >
      <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5">
          <div className="text-[11px] tracking-[0.25em] uppercase font-bold text-[#FF6B6B] mb-4">
            ✦ Share your review
          </div>
          <h2 className="font-display font-black text-3xl sm:text-5xl md:text-6xl text-[#0B132B] tracking-tighter leading-[0.95]">
            Tell us what you <br />
            <span className="font-script font-medium text-[#4ECDC4]">loved the most.</span>
          </h2>
          <p className="mt-6 text-lg text-[#3A506B] leading-relaxed max-w-md">
            Your feedback helps us fine-tune every class, every week. Drop a quick note and help the next batch shine.
          </p>

          <div className="mt-10 space-y-4">
            {[
              "Takes less than a minute",
              "Read by the core team",
              "Helps improve every batch",
            ].map((t) => (
              <div key={t} className="flex items-center gap-3 text-[#1C2541]">
                <CheckCircle2 size={18} className="text-[#4ECDC4]" />
                <span>{t}</span>
              </div>
            ))}
          </div>
        </div>

        <form
          onSubmit={submit}
          data-testid="enrollment-form"
          className="lg:col-span-7 bg-white rounded-3xl p-6 sm:p-8 md:p-10 border border-[#0B132B]/8 shadow-[0_12px_40px_rgba(11,19,43,0.06)]"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="md:col-span-1">
              <label className="text-xs tracking-widest uppercase font-bold text-[#758A9F]">Your Name</label>
              <input
                type="text"
                name="name"
                value={form.name}
                onChange={change("name")}
                placeholder="e.g. Aarav Kapoor"
                className={`mt-2 ${inputBase}`}
                data-testid="form-name"
                required
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs tracking-widest uppercase font-bold text-[#758A9F]">Your feedback</label>
              <textarea
                rows={4}
                name="feedback"
                value={form.feedback}
                onChange={change("feedback")}
                placeholder="Share what stood out, what helped most, or what we can do better."
                className={`mt-2 ${inputBase}`}
                data-testid="form-feedback"
                required
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="form-submit"
            className="mt-8 btn-pill bg-[#0B132B] hover:bg-[#1C2541] text-white shadow-[0_12px_30px_rgba(11,19,43,0.20)] disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <Loader2 size={18} className="animate-spin" /> Sending...
              </>
            ) : success ? (
              <>
                <CheckCircle2 size={18} /> Sent — send another?
              </>
            ) : (
              <>
                <Send size={16} /> Submit review
              </>
            )}
          </button>
        </form>
      </div>
    </section>
  );
}

